// Temporary compatibility layer for legacy useInvoicePermissions hook
// TODO: Migrate all usages to the new useWorkflowPermissions hook

import { useCallback, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { InvoiceRecord } from '@types';
import {
  shouldBlockUserForSeparationOfDuties,
  canEditWorkflowFields as canEditWorkflowFieldsUtil
} from '@utils/separationOfDuties';
import { safeIdComparison } from '@utils/idComparison';
import { isWorkflowFieldDisabled as isWorkflowFieldDisabledUtil } from '@utils/workflowFieldPermissions';
import { UserRole } from '@config/workflow-config';
import { InvoiceStatus, InvoiceClassification } from '@config/invoice-config';
import { useWorkflowPermissions } from './useWorkflowPermissions';

interface UseInvoicePermissionsProps {
  currentStatus: string;
  invoice: InvoiceRecord | undefined;
  auditData: any;
}

// Re-export the safeIdComparison utility
export { safeIdComparison } from '@utils/idComparison';

// Convert string status to enum
const statusMap: Record<string, InvoiceStatus> = {
  'unprocessed': InvoiceStatus.UNPROCESSED,
  'adding': InvoiceStatus.ADDING,
  'new_products': InvoiceStatus.NEW_PRODUCTS,
  'ir': InvoiceStatus.IR,
  'invoicing': InvoiceStatus.INVOICING,
  'head_office_stock': InvoiceStatus.HEAD_OFFICE_STOCK,
  'expense_invoices': InvoiceStatus.EXPENSE_INVOICES,
  'entering_stock': InvoiceStatus.ENTERING_STOCK,
  'entering_expense': InvoiceStatus.ENTERING_EXPENSE,
  'credit_note': InvoiceStatus.CREDIT_NOTE,
  'credit_entering': InvoiceStatus.CREDIT_ENTERING,
  'completed': InvoiceStatus.COMPLETED
};

// Convert string role to enum
const roleMap: Record<string, UserRole> = {
  'admin': UserRole.ADMINISTRATOR,
  'administrator': UserRole.ADMINISTRATOR,
  'finance': UserRole.FINANCE,
  'stock': UserRole.STOCK
};

export const useInvoicePermissions = ({
  currentStatus,
  invoice,
  auditData
}: UseInvoicePermissionsProps) => {
  const { user } = useAuth();

  // Get user role from user object
  const getUserRole = useCallback((): UserRole => {
    if (user?.roles?.some(role => role.name === 'admin' || role.name === 'administrator')) {
      return UserRole.ADMINISTRATOR;
    }
    if (user?.roles?.some(role => role.name === 'finance')) {
      return UserRole.FINANCE;
    }
    if (user?.roles?.some(role => role.name === 'stock')) {
      return UserRole.STOCK;
    }
    return UserRole.STOCK; // default fallback
  }, [user?.roles]);

  // Convert current status to enum
  const enumStatus = statusMap[currentStatus] || InvoiceStatus.UNPROCESSED;
  const userRole = getUserRole();

  // Use the new workflow permissions system
  useWorkflowPermissions(
    enumStatus,
    (invoice?.classification as InvoiceClassification) || 'deliver_direct',
    userRole,
    user?.id,
    invoice?.assigned_to
  );

  // Memoized role checking functions
  const hasRole = useCallback((roleName: string): boolean => {
    const currentUserRole = getUserRole();
    const requestedRole = roleMap[roleName.toLowerCase()];
    return currentUserRole === requestedRole;
  }, [getUserRole]);

  const canEditCreditNoteFields = useCallback((): boolean => {
    // Admin, Finance, and Stock roles can edit credit note fields
    return hasRole('admin') || hasRole('finance') || hasRole('stock');
  }, [hasRole]);

  const canEditStockFields = useCallback((): boolean => {
    // Admin, Finance, and Stock roles can edit stock-related fields
    return hasRole('admin') || hasRole('finance') || hasRole('stock');
  }, [hasRole]);

  const canEditUnprocessedFields = useCallback((): boolean => {
    // For unprocessed status: only Admin and Finance roles can edit
    return hasRole('admin') || hasRole('finance');
  }, [hasRole]);

  const canEditAddingBasicFields = useCallback((): boolean => {
    // For adding status: Admin and Finance roles can edit basic fields
    return hasRole('admin') || hasRole('finance');
  }, [hasRole]);

  const canEditAddingStockFields = useCallback((): boolean => {
    // For adding status: Admin, Finance, and Stock roles can edit stock-related fields
    return hasRole('admin') || hasRole('finance') || hasRole('stock');
  }, [hasRole]);

  // Memoized function to get the user who moved the invoice to a specific status
  const getUserWhoMovedToStatus = useCallback((targetStatus: string) => {
    // If audit data is not loaded yet, return null (don't block based on incomplete data)
    if (!auditData?.logs) return null;

    // Find the most recent status change to the target status
    const statusChangeLog = auditData.logs.find((log: any) =>
      log.action === 'status_change' &&
      log.changes?.after?.status === targetStatus
    );

    return statusChangeLog?.user_id;
  }, [auditData?.logs]);

  const canEditWorkflowFields = useCallback((): boolean => {
    const isAdmin = hasRole('admin');
    const isFinance = hasRole('finance');
    // Use safe ID comparison to handle type inconsistencies
    const isAssignedUser = safeIdComparison(invoice?.assigned_to_user?.id, user?.id);

    // Use the utility function for workflow field editing logic
    const canEdit = canEditWorkflowFieldsUtil({
      currentStatus,
      userId: user?.id,
      isAdmin,
      isFinance,
      isAssignedUser,
      getUserWhoMovedToStatus
    });

    // If not assignment-required status, use normal role-based permissions
    if (currentStatus !== 'adding' && currentStatus !== 'ir' && currentStatus !== 'invoicing') {
      return canEditStockFields();
    }

    return canEdit;
  }, [currentStatus, hasRole, invoice?.assigned_to_user?.id, user?.id, getUserWhoMovedToStatus, canEditStockFields]);

  const canEditBasicFields = useCallback((): boolean => {
    if (currentStatus === 'unprocessed') {
      return canEditUnprocessedFields(); // Only AF roles can edit
    }
    return canEditWorkflowFields(); // Use normal workflow permissions for other statuses
  }, [currentStatus, canEditUnprocessedFields, canEditWorkflowFields]);

  // Special permission function for specific fields in 'adding' status
  const canEditAddingStatusFields = useCallback((): boolean => {
    // For 'adding' status: fields are read-only unless assigned to user
    if (currentStatus === 'adding') {
      const isAdmin = hasRole('admin');
      const isFinance = hasRole('finance');
      // Use safe ID comparison to handle type inconsistencies
      const isAssignedUser = safeIdComparison(invoice?.assigned_to_user?.id, user?.id);

      // Use the utility function for separation of duties check
      if (shouldBlockUserForSeparationOfDuties({
        currentStatus: 'adding',
        userId: user?.id,
        isAdmin,
        isFinance,
        getUserWhoMovedToStatus
      })) {
        return false;
      }

      // Allow editing if admin, finance, or assigned to current user
      return isAdmin || isFinance || isAssignedUser;
    }

    // For other statuses, fall back to normal workflow permissions
    return canEditWorkflowFields();
  }, [currentStatus, hasRole, invoice?.assigned_to_user?.id, user?.id, getUserWhoMovedToStatus, canEditWorkflowFields]);

  // Memoized helper functions to determine field disabled state based on status and role
  const isBasicFieldDisabled = useMemo((): boolean => {
    // Basic fields logic based on status and role
    if (currentStatus === 'unprocessed') {
      return !canEditUnprocessedFields(); // Only AF roles can edit
    }
    if (currentStatus === 'adding') {
      return !canEditAddingBasicFields(); // Admin and Finance can edit
    }
    // For statuses 3-12, Admin and Finance can edit basic fields
    return !(hasRole('admin') || hasRole('finance'));
  }, [currentStatus, canEditUnprocessedFields, canEditAddingBasicFields, hasRole]);

  const isWorkflowFieldDisabled = useCallback((fieldType: 'store' | 'po' | 'so' | 'ir' | 'inv'): boolean => {
    // Use the simplified utility function for workflow field permissions
    return isWorkflowFieldDisabledUtil(fieldType, {
      currentStatus,
      hasRole,
      canEditWorkflowFields,
    });
  }, [currentStatus, hasRole, canEditWorkflowFields]);

  const isNotesFieldDisabled = useMemo((): boolean => {
    // All roles (admin, finance, stock) can edit notes in all statuses
    return !(hasRole('admin') || hasRole('finance') || hasRole('stock'));
  }, [hasRole]);

  const isCreditNoteFieldDisabled = useMemo((): boolean => {
    if (currentStatus === 'credit_note') {
      return !canEditCreditNoteFields(); // AFS can edit
    }
    // For credit_entering and completed, Admin and Finance can edit
    return !(hasRole('admin') || hasRole('finance'));
  }, [currentStatus, canEditCreditNoteFields, hasRole]);

  // Helper function to check if the current user was previously assigned to this invoice
  const wasUserPreviouslyAssigned = useMemo(() => {
    // Use safe ID comparison to handle all type inconsistencies
    return safeIdComparison(user?.id, invoice?.previous_assignee);
  }, [user?.id, invoice?.previous_assignee]);

  return {
    // Role checks
    hasRole,
    canEditCreditNoteFields,
    canEditStockFields,
    canEditUnprocessedFields,
    canEditAddingBasicFields,
    canEditAddingStockFields,
    canEditWorkflowFields,
    canEditBasicFields,
    canEditAddingStatusFields,

    // Field state checks
    isBasicFieldDisabled,
    isWorkflowFieldDisabled,
    isNotesFieldDisabled,
    isCreditNoteFieldDisabled,
    wasUserPreviouslyAssigned,

    // Utility functions
    getUserWhoMovedToStatus
  };
};